import { createFileRoute } from "@tanstack/react-router";
import Link from "~/components/Link";
import {
  NewStudentButton,
  NewReportTypeButton,
} from "~/components/CreateButtons";
import { WithStudentNav } from "~/components/WithStudentNav";
import { trpc } from "~/utils/trpc.client";

/**
 * Team home / recent students (was (new)/[teamId]/(with-student-nav)/page.tsx).
 * Lived under the (with-student-nav) group → wrap content in <WithStudentNav>.
 */
export const Route = createFileRoute("/_authed/$teamId/")({
  component: TeamPage,
});

function TeamPage() {
  const { teamId } = Route.useParams();

  const me = trpc.auth.me.useQuery();
  const personId = me.data?.id;

  const studentsQuery = trpc.students.getRecentStudents.useQuery({ teamId });
  const reportTypesQuery = trpc.reportTypes.getReportTypes.useQuery({ teamId });
  const roleQuery = trpc.team.getTeamRole.useQuery(
    { teamId, personId: personId ?? "" },
    { enabled: !!personId },
  );

  const students = studentsQuery.data ?? [];
  const reportTypes = reportTypesQuery.data ?? [];
  const role = roleQuery.data;

  return (
    <WithStudentNav>
      <div className="relative m-auto flex h-full w-full max-w-xl flex-col gap-4 p-4">
        {/* Welcome message */}
        <div className="card bg-base-100 shadow">
          <div className="card-body">
            <div className="flex flex-col gap-2">
              <h2 className="card-title">Welcome to SimpleIEP!</h2>
              <p className="text-sm text-gray-600">
                SimpleIEP is a tool for teachers to easily write and manage IEPs
                for their students. If you have any questions or feedback,
                please reach out to us at{" "}
                <a
                  href="mailto:hello@simple-iep.com"
                  className="link link-hover underline"
                >
                  hello@simple-iep.com
                </a>
                .
              </p>
            </div>
          </div>
        </div>
        {/* Students */}
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">Recent Students</h1>
          <NewStudentButton teamId={teamId} className="btn btn-primary btn-sm">
            New Student
          </NewStudentButton>
        </div>
        <div className="flex">
          {students.length === 0 ? (
            <div className="flex h-full w-full place-content-center place-items-center italic opacity-70">
              No recent students
            </div>
          ) : (
            <div className="flex flex-col gap-4 w-full">
              {students.map((student) => (
                <Link
                  key={student.id}
                  className="card bg-base-100 link no-underline shadow"
                  href={`/${teamId}/students/${student.id}`}
                >
                  <div className="card-body p-6">
                    <div className="flex items-center">
                      <div className="avatar avatar-placeholder w-10">
                        <div className="text-primary-content w-24 rounded-full bg-sky-600">
                          <span className="">
                            {student.name
                              .split(" ")
                              .map((name) => name[0])
                              .join("")
                              .toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="ml-3">
                        <h2 className="">{student.name}</h2>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
        {/* Report types */}
        {role?.roleId === "admin" && (
          <div className="flex flex-col gap-4">
            <div className="mt-8 flex justify-between ">
              <h2 className="text-xl font-bold">Report Types</h2>

              <div className="flex gap-4">
                <NewReportTypeButton
                  teamId={teamId}
                  className="btn btn-primary btn-sm"
                >
                  New Report Type
                </NewReportTypeButton>
              </div>
            </div>
            {reportTypes.map((reportType) => (
              <Link
                key={reportType.id}
                href={`/${teamId}/report-types/${reportType.id}`}
                className="card bg-base-100  shadow"
              >
                <div className="card-body">
                  <div className="flex items-center">
                    {/* Report name and last updated date */}
                    <div className="flex flex-col gap-2">
                      <h3 className="card-title">{reportType.name}</h3>
                      <p className="text-sm text-gray-600">
                        Last updated {reportType.updatedAt.toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
            {/* If no reports, show an empty state that's full width and 5rem tall that says "No reports" */}
            {reportTypes.length === 0 && (
              <div className="flex h-full min-h-[6rem] w-full place-content-center place-items-center italic opacity-70">
                No report types have been created yet.
              </div>
            )}
          </div>
        )}
      </div>
    </WithStudentNav>
  );
}
